import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../Component/Sidebar';
import { 
  fetchSingleTransactionThunk, 
  updateTransactionThunk 
} from '../features/transactions/transactionThunks';
import { resetSelectedTransaction } from '../features/transactions/transactionSlice';
import '../CSS/SingleTransactionPage.css';

const SingleTransactionPage = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get from redux store
  const { 
    selectedTransaction: transaction, 
    singleLoading: loading,
    error 
  } = useSelector((state) => state.transactions);
  
  useEffect(() => {
    // Fetch the transaction
    dispatch(fetchSingleTransactionThunk(transactionId));
    
    // Cleanup on unmount
    return () => {
      dispatch(resetSelectedTransaction());
    };
  }, [transactionId, dispatch]);

  const handleVerify = () => {
    if (!transaction) return;
    
    dispatch(
      updateTransactionThunk({
        transactionId: transaction.id,
        updates: { verified: true },
      })
    ).then(() => {
      alert('Transaction verified!');
      navigate(-1); // go back
    }).catch(err => {
      console.error('Failed to verify transaction:', err);
      alert('Failed to verify transaction. Please try again.');
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Format date to display as "12th Aug 2025, 11:49pm"
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    // Get day with suffix (1st, 2nd, 3rd, etc.)
    const day = date.getDate();
    const suffix = getDaySuffix(day);
    
    // Get month abbreviation
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    
    // Get year
    const year = date.getFullYear();
    
    // Get time in 12-hour format with am/pm
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert hour '0' to '12'
    
    return `${day}${suffix} ${month} ${year}, ${hours}:${minutes}${ampm}`;
  };
  
  // Helper function to get day suffix (1st, 2nd, 3rd, etc.)
  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Skeleton loading component for the transaction details
  const TransactionSkeleton = () => (
    <div className="survill-transaction-container">
      <div className="survill-transaction-header">
        <div className="survill-back-button skeleton"></div>
        <div className="skeleton-text-large"></div>
        <div className="survill-status-flag skeleton"></div>
      </div>

      <div className="survill-transaction-layout">
        {/* Main Video Player Section Skeleton */}
        <div className="survill-main-video-section">
          <div className="survill-video-player-container">
            <div className="skeleton-text-medium"></div>
            <div className="survill-video-player skeleton-video"></div>
            <div className="survill-video-controls">
              <div className="skeleton-text-small"></div>
              <div className="skeleton-text-small"></div>
            </div>
          </div>
        </div>

        {/* Store Details Section Skeleton */}
        <div className="survill-store-section">
          <div className="survill-store-banner skeleton"></div>
          <div className="survill-transaction-details">
            <div className="survill-details-header">
              <div className="skeleton-text-medium"></div>
              <div className="skeleton skeleton-device"></div>
            </div>
            <div className="survill-details-grid">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="survill-detail-item">
                  <span className="skeleton-text-small"></span>
                  <span className="skeleton-text-small"></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // If loading, show skeleton
  if (loading) {
    return (
      <div className="index-box">
        <Sidebar />
        <TransactionSkeleton />
      </div>
    );
  }

  // If error, show error message
  if (error || !transaction) {
    return (
      <div className="index-box">
        <Sidebar />
        <div className="survill-transaction-container">
          <div className="survill-transaction-header">
            <button className="survill-back-button" onClick={handleBack}>
              Back
            </button>
            <h2>Error</h2>
          </div>
          <div className="survill-error-container">
            <p>{error || 'Transaction not found'}</p>
            <button className="survill-retry-button" onClick={() => dispatch(fetchSingleTransactionThunk(transactionId))}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="index-box">
      <Sidebar />
      <div className="survill-transaction-container">
        <div className="survill-transaction-header">
          <button className="survill-back-button" onClick={handleBack}>
            Back
          </button>
          <h2>Transaction Details</h2>
          <div className="survill-transaction-status">
            {transaction.flagged && <span className="survill-status-flag">Flagged</span>}
            {transaction.verified ? (
              <span className="survill-status-verified">Verified</span>
            ) : (
              <button className="survill-verify-button" onClick={handleVerify}>
                Verify Transaction
              </button>
            )}
          </div>
        </div>

        <div className="survill-transaction-layout">
          {/* Main Video Player Section */}
          <div className="survill-main-video-section">
            <div className="survill-video-player-container">
              <h3>Video Evidence</h3>
              <div className="survill-video-player">
                {transaction.video_evidence ? (
                  <video controls width="100%">
                    <source src={transaction.video_evidence} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="survill-no-video">No video evidence available</div>
                )}
              </div>
              <div className="survill-video-controls">
                <div className="survill-transaction-id">
                  Transaction #{transaction.transaction_id}
                </div>
                <div className="survill-video-timestamp">
                  {formatDate(transaction.date)}
                </div>
              </div>
            </div>
          </div>

          {/* Store Details Section */}
          <div className="survill-store-section">
            {transaction.store && (
              <>
                <div className="survill-store-banner">
                  <img src={transaction.store.image} alt={transaction.store.store_name} />
                  <div className="survill-store-overlay">
                    <h3>{transaction.store.store_name}</h3>
                    <p className="survill-store-location">{transaction.store.store_city}</p>
                  </div>
                </div>
                <div className="survill-transaction-details">
                  <div className="survill-details-header">
                    <h3>Transaction Information</h3>
                    <div className="survill-device-info">{transaction.device}</div>
                  </div>
                  <div className="survill-details-grid">
                    <div className="survill-detail-item">
                      <span className="survill-detail-label">Transaction ID</span>
                      <span className="survill-detail-value">{transaction.transaction_id}</span>
                    </div>
                    <div className="survill-detail-item">
                      <span className="survill-detail-label">Transaction Date</span>
                      <span className="survill-detail-value">{formatDate(transaction.date)}</span>
                    </div>
                    <div className="survill-detail-item">
                      <span className="survill-detail-label">Store ID</span>
                      <span className="survill-detail-value">{transaction.store.id}</span>
                    </div>
                    <div className="survill-detail-item">
                      <span className="survill-detail-label">Description</span>
                      <span className="survill-detail-value">{transaction.description}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleTransactionPage;